/**
 * Party — wallet, budget pools, loot.
 *
 * First read-path slice: fetches the party snapshot + loot list via the
 * SessionRead HTTP lambda and renders it static. Mutations (wallet deposit,
 * pool create, loot transfer) land in the next slice over the WebSocket
 * $default dispatcher — this page is currently read-only.
 *
 * The page is intentionally dumb about shape: it defensive-renders whatever
 * the API returns, so a partially-seeded party or a pool with missing
 * optional fields won't crash the view.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  AuditEntry,
  DEFAULT_SESSION_ID,
  getAuditLog,
  getPartySnapshot,
  PartySnapshot,
} from '../lib/sessionApi';
import {
  BudgetPool,
  LootItem,
  Wallet,
  unallocatedWallet,
} from '../lib/party';

/**
 * Render a wallet as the four coin columns, muted when zero. Uses the
 * JetBrains Mono stack via the .num class from the design system.
 */
function WalletLine({ wallet }: { wallet: Wallet | undefined }) {
  const w = wallet ?? { cp: 0, sp: 0, gp: 0, pp: 0 };
  const coins: Array<[keyof Wallet, string]> = [
    ['pp', 'pp'],
    ['gp', 'gp'],
    ['sp', 'sp'],
    ['cp', 'cp'],
  ];
  return (
    <span className="num" style={{ display: 'inline-flex', gap: '0.75rem' }}>
      {coins.map(([k, label]) => (
        <span
          key={k}
          style={{
            color: w[k] > 0 ? 'var(--fg)' : 'var(--fg-muted)',
          }}
        >
          {w[k]} <span style={{ color: 'var(--fg-muted)' }}>{label}</span>
        </span>
      ))}
    </span>
  );
}

function PoolRow({ pool }: { pool: BudgetPool }) {
  const scope =
    pool.ownerScope === 'party'
      ? 'Party'
      : `Character · ${pool.ownerScope.character}`;
  return (
    <div
      style={{
        padding: '0.75rem 1rem',
        border: '1px solid var(--border)',
        borderRadius: '6px',
        marginBottom: '0.5rem',
        opacity: pool.archived ? 0.5 : 1,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <strong>{pool.name}</strong>
        <span style={{ fontSize: '0.85rem', color: 'var(--fg-muted)' }}>{scope}</span>
      </div>
      {pool.description && (
        <p style={{ margin: '0.25rem 0', color: 'var(--fg-muted)', fontSize: '0.9rem' }}>
          {pool.description}
        </p>
      )}
      <div style={{ marginTop: '0.25rem' }}>
        <WalletLine wallet={pool.currentAmount} />
        {pool.targetAmount && (
          <span style={{ marginLeft: '0.75rem', color: 'var(--fg-muted)' }}>
            / <WalletLine wallet={pool.targetAmount} />
          </span>
        )}
      </div>
    </div>
  );
}

function LootRow({ item }: { item: LootItem }) {
  const display = item.identified ? item.name : `Unknown ${(item.tags?.[0]) ?? 'item'}`;
  return (
    <tr>
      <td>{display}</td>
      <td className="num" style={{ textAlign: 'right' }}>{item.quantity}</td>
      <td style={{ color: 'var(--fg-muted)' }}>{item.source ?? ''}</td>
      <td style={{ color: 'var(--fg-muted)' }}>{item.tags?.join(', ') ?? ''}</td>
    </tr>
  );
}

export default function Party() {
  const [snapshot, setSnapshot] = useState<PartySnapshot | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [auditCursor, setAuditCursor] = useState<string | null>(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const data = await getPartySnapshot(DEFAULT_SESSION_ID);
      setSnapshot(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load party');
    } finally {
      setLoading(false);
    }
  }, []);

  // Audit log loads independently of the party snapshot — it's a separate
  // partition read and a slow audit endpoint shouldn't block the party view
  // from rendering.
  const loadInitialAudit = useCallback(async () => {
    setAuditError(null);
    setAuditLoading(true);
    try {
      const page = await getAuditLog(DEFAULT_SESSION_ID, { limit: 50 });
      setAudit(page.entries);
      setAuditCursor(page.nextBefore);
    } catch (err) {
      setAuditError(err instanceof Error ? err.message : 'Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const loadOlderAudit = useCallback(async () => {
    if (!auditCursor) return;
    setAuditLoading(true);
    try {
      const page = await getAuditLog(DEFAULT_SESSION_ID, { limit: 50, before: auditCursor });
      setAudit((prev) => [...prev, ...page.entries]);
      setAuditCursor(page.nextBefore);
    } catch (err) {
      setAuditError(err instanceof Error ? err.message : 'Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  }, [auditCursor]);

  useEffect(() => {
    refresh();
    loadInitialAudit();
  }, [refresh, loadInitialAudit]);

  if (loading) {
    return <p>Loading party…</p>;
  }
  if (error) {
    return <p style={{ color: 'var(--danger, #e06c75)' }}>Error: {error}</p>;
  }
  if (!snapshot) {
    return <p>No party data.</p>;
  }

  const { party, loot } = snapshot;
  const unallocated = unallocatedWallet(party);
  const activePools = party.pools.filter((p) => !p.archived);
  const archivedPools = party.pools.filter((p) => p.archived);

  return (
    <div style={{ maxWidth: '900px' }}>
      <h1>Party</h1>

      <section style={{ marginBottom: '2rem' }}>
        <h2>Wallet</h2>
        <div style={{ marginBottom: '0.5rem' }}>
          <span style={{ color: 'var(--fg-muted)', marginRight: '0.5rem' }}>Total:</span>
          <WalletLine wallet={party.wallet} />
        </div>
        <div>
          <span style={{ color: 'var(--fg-muted)', marginRight: '0.5rem' }}>Unallocated:</span>
          <WalletLine wallet={unallocated} />
        </div>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2>Budget pools</h2>
        {activePools.length === 0 && (
          <p style={{ color: 'var(--fg-muted)' }}>No active pools.</p>
        )}
        {activePools.map((p) => (
          <PoolRow key={p.id} pool={p} />
        ))}
        {archivedPools.length > 0 && (
          <details style={{ marginTop: '1rem' }}>
            <summary style={{ cursor: 'pointer', color: 'var(--fg-muted)' }}>
              Archived ({archivedPools.length})
            </summary>
            <div style={{ marginTop: '0.5rem' }}>
              {archivedPools.map((p) => (
                <PoolRow key={p.id} pool={p} />
              ))}
            </div>
          </details>
        )}
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2>Activity</h2>
        {auditError && (
          <p style={{ color: 'var(--danger, #e06c75)' }}>Audit error: {auditError}</p>
        )}
        {audit.length === 0 && !auditLoading && !auditError && (
          <p style={{ color: 'var(--fg-muted)' }}>No activity yet.</p>
        )}
        {audit.length > 0 && (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {audit.map((e) => (
              <li
                key={e.id}
                style={{
                  padding: '0.5rem 0',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'baseline',
                }}
              >
                <span className="num" style={{ color: 'var(--fg-muted)', fontSize: '0.85rem', minWidth: '11rem' }}>
                  {new Date(e.ts).toLocaleString()}
                </span>
                <span style={{ color: 'var(--fg-muted)', minWidth: '7rem' }}>
                  {e.actorUsername}
                </span>
                <span style={{ flex: 1 }}>
                  {e.summary}
                  {e.reason && (
                    <span style={{ color: 'var(--fg-muted)', marginLeft: '0.5rem' }}>
                      ({e.reason})
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
        {auditCursor && (
          <button
            onClick={loadOlderAudit}
            disabled={auditLoading}
            style={{ marginTop: '0.75rem' }}
          >
            {auditLoading ? 'Loading…' : 'Load older'}
          </button>
        )}
      </section>

      <section>
        <h2>Loot ({loot.length})</h2>
        {loot.length === 0 ? (
          <p style={{ color: 'var(--fg-muted)' }}>No loot yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border)' }}>
                <th>Item</th>
                <th style={{ textAlign: 'right' }}>Qty</th>
                <th>Source</th>
                <th>Tags</th>
              </tr>
            </thead>
            <tbody>
              {loot.map((item) => (
                <LootRow key={item.id} item={item} />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
